// PollEditor — the Poll block editor (a NEW Build-Menu block, Premium tier).
// Fable Hail Mary Wave 5 (Lane-N, BLOCK-FORMS). Deck source of truth:
// concepts/builder-blocks/03-forms-poll-block + 04-viewwheel-poll-live + index.md.
//
// Deck-mandated feature set (03): Question field; drag-to-reorder option rows
// (grip + text + remove); "Option outline & fill" via the shared brand colour
// control (Shared/ColorPopup — Preset/Custom/Wheel Palette/Segment Palette,
// #6D5EF6 default); "Multiple votes" + "Show results" switches. Preview: the
// selected option uses that outline/fill colour, Vote button #8F38EF.
//
// PREMIUM: the block is a Premium feature — the FUNCTIONAL gate (collect/return
// votes) is SERVER-checked & fail-closed (BE enforcement via SubscriptionService).
// The authoring surface itself ships unflagged (Gordon's flag policy). A small
// "Premium" note rides the heading so the author knows the tier at a glance; the
// upgrade path is the app-standard openUpgradePopup, wired at the block level.
//
// Controlled: `value` (poll model) + `onChange(next)`. Renders standalone in the
// isolated harness AND drives the live craftjs Poll widget via its Settings bridge.

import React, { useState } from "react";
import styled from "styled-components";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import AdvancedOptions from "../AdvancedOptions";
import { TextInputField, Toggle, QuadStepper } from "../controls";
import ColorPopup from "../../Shared/ColorPopup";
import { maxLength, validateField } from "../validation";
import { fontFamily } from "../../../../theme/tokens";
import { fieldChrome } from "../EditorField";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const BORDER = fieldChrome.border;
const PURPLE = fieldChrome.accent;
const DANGER = fieldChrome.error;

const QUESTION_MAX = 200;
const OPTION_MAX = 80;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 8;
// Same drag-and-drop machinery ListTab/ListItem.jsx uses for its reorderable
// rows (useDrag + useDrop over a shared DndProvider — see App.js) rather than
// a bespoke implementation.
const OPTION_DRAG_TYPE = "poll-editor-option";

export const POLL_DEFAULT = {
  question: "",
  options: [
    { id: "opt-1", text: "" },
    { id: "opt-2", text: "" },
  ],
  optionColor: "#6D5EF6", // deck: option outline & fill default
  allowMultiple: false, // "Multiple votes"
  showResults: true, // "Show results"
  // fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458: Poll
  // had no margin prop/control at all — see the Spacing section below.
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
};

const PollGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="13" width="4" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
    <rect x="10" y="8" width="4" height="12" rx="1" stroke="currentColor" strokeWidth="1.6" />
    <rect x="16" y="4" width="4" height="16" rx="1" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

// Mirrors the Premium-block "P" tile badge (Shared/Card/Card.jsx) — same
// background/color/radius — instead of a plain text pill.
const PremiumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 16px;
  height: 16px;
  border-radius: 5px;
  background: #ede4fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
`;
const LiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr 44px;
  align-items: start;
  gap: 8px;
  font-family: ${fontFamily};
`;
const Grip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  color: ${MUTED};
  cursor: grab;
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; border-radius: 4px; }
`;
const IconBtn = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  color: ${DANGER};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: #f6f7f9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const AddBtn = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border: 1px dashed ${PURPLE};
  border-radius: 8px;
  background: #faf7fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const Hint = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${MUTED};
  line-height: 1.4;
`;

const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <g fill="currentColor">
      <circle cx="5" cy="3" r="1.1" /><circle cx="9" cy="3" r="1.1" />
      <circle cx="5" cy="7" r="1.1" /><circle cx="9" cy="7" r="1.1" />
      <circle cx="5" cy="11" r="1.1" /><circle cx="9" cy="11" r="1.1" />
    </g>
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 7h14M10 7V5h4v2M6 7l1 12h10l1-12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

let optionSeq = 100;
const newOption = () => ({ id: `opt-${Date.now()}-${optionSeq++}`, text: "" });

// Draggable option row: same useDrag/useDrop pairing as ListTab/ListItem.jsx
// (drop target's `hover` reorders live as the dragged row passes over a
// sibling; the drag source's `end` fires once the drop settles).
const OptionRowDraggable = ({ id, index, moveOption, onDropped, children }) => {
  const ref = React.useRef(null);

  const [, drop] = useDrop({
    accept: OPTION_DRAG_TYPE,
    hover(dragged) {
      if (dragged.index === index) return;
      moveOption(dragged.index, index);
      dragged.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: OPTION_DRAG_TYPE,
    item: { id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    end: (item) => onDropped(item.index),
  });

  drag(drop(ref));

  return (
    <OptionRow ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      {children}
    </OptionRow>
  );
};

const PollEditor = ({
  value = POLL_DEFAULT,
  onChange = () => {},
  segmentName,
  onBack,
  status = "ready",
  onRetry,
  errorMessage,
}) => {
  const v = { ...POLL_DEFAULT, ...value, options: value?.options?.length ? value.options : POLL_DEFAULT.options };
  const patch = (p) => onChange({ ...v, ...p });
  const [announcement, setAnnouncement] = useState("");

  const questionError = validateField(v.question, [
    maxLength(QUESTION_MAX, `Keep the question under ${QUESTION_MAX} characters.`),
  ]);

  const setOption = (id, text) =>
    patch({ options: v.options.map((o) => (o.id === id ? { ...o, text } : o)) });
  const removeOption = (id) =>
    patch({ options: v.options.filter((o) => o.id !== id) });
  const addOption = () => {
    if (v.options.length >= MAX_OPTIONS) return;
    patch({ options: [...v.options, newOption()] });
  };
  // Single source of truth for reordering — both the mouse drag (hover, as
  // the dragged row passes a sibling) and the Alt+Up/Down keyboard path move
  // the option to an absolute index, so the block node's persisted order is
  // computed identically either way.
  const moveOption = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= v.options.length) return;
    const next = v.options.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    patch({ options: next });
  };
  const announceOptionMoved = (toIndex) =>
    setAnnouncement(`Option moved to position ${toIndex + 1} of ${v.options.length}.`);
  const setMarginSide = (side, val) => patch({ margin: { ...v.margin, [side]: val } });
  const moveByKey = (idx, dir) => {
    const toIndex = idx + dir;
    if (toIndex < 0 || toIndex >= v.options.length) return;
    moveOption(idx, toIndex);
    announceOptionMoved(toIndex);
  };

  return (
    // Local DndProvider (same pattern as ListTab/SegmentsTab/SegmentResizeView):
    // App.js already wraps the live app in one, but PollEditor also renders
    // standalone in the isolated harness with no ancestor provider — nesting
    // is harmless (react-dnd supports it) and makes the option rows' drag
    // source/drop target usable in both contexts.
    <DndProvider backend={HTML5Backend}>
      <BlockEditorPane
        blockName="Poll"
        segmentName={segmentName}
        glyph={<PollGlyph />}
        onBack={onBack}
        status={status}
        onRetry={onRetry}
        errorMessage={errorMessage}
      >
      <EditorSection heading={<span>Basic<PremiumBadge aria-label="Premium">P</PremiumBadge></span>}>
        <TextInputField
          label="Question"
          value={v.question}
          onChange={(s) => patch({ question: s })}
          error={questionError}
          counterMax={QUESTION_MAX}
          placeholder="Ask your audience…"
        />

        <div>
          <div style={{ fontFamily, fontSize: 12, fontWeight: 600, color: INK, marginBottom: 8 }}>
            Options
          </div>
          <LiveRegion aria-live="polite">{announcement}</LiveRegion>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {v.options.map((o, idx) => (
              <OptionRowDraggable
                key={o.id}
                id={o.id}
                index={idx}
                moveOption={moveOption}
                onDropped={announceOptionMoved}
              >
                <Grip
                  role="button"
                  tabIndex={0}
                  aria-label={`Reorder option ${idx + 1}`}
                  title="Drag to reorder — or press Alt+Up / Alt+Down"
                  onKeyDown={(e) => {
                    if (!e.altKey) return;
                    if (e.key === "ArrowUp") { e.preventDefault(); moveByKey(idx, -1); }
                    if (e.key === "ArrowDown") { e.preventDefault(); moveByKey(idx, +1); }
                  }}
                >
                  <GripIcon />
                </Grip>
                <TextInputField
                  aria-label={`Option ${idx + 1}`}
                  value={o.text}
                  onChange={(s) => setOption(o.id, s)}
                  counterMax={OPTION_MAX}
                  error={validateField(o.text, [maxLength(OPTION_MAX, `Under ${OPTION_MAX} characters.`)])}
                  placeholder={`Option ${idx + 1}`}
                />
                <IconBtn
                  type="button"
                  aria-label={`Remove option ${idx + 1}`}
                  disabled={v.options.length <= MIN_OPTIONS}
                  onClick={() => removeOption(o.id)}
                >
                  <TrashIcon />
                </IconBtn>
              </OptionRowDraggable>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <AddBtn type="button" onClick={addOption} disabled={v.options.length >= MAX_OPTIONS}>
              + Add option
            </AddBtn>
          </div>
          {v.options.length >= MAX_OPTIONS && (
            <Hint style={{ marginTop: 6 }}>Up to {MAX_OPTIONS} options.</Hint>
          )}
        </div>

        <ColorPopup
          title="Option outline & fill"
          background={v.optionColor}
          handleChangeBackgroundColor={(c) => patch({ optionColor: c })}
          resetBackgroundColor={() => patch({ optionColor: POLL_DEFAULT.optionColor })}
          showSlider={false}
        />
      </EditorSection>

      <AdvancedOptions>
        <EditorSection heading="Voting" dense>
          <Toggle
            label="Allow multiple votes"
            checked={v.allowMultiple}
            onChange={(b) => patch({ allowMultiple: b })}
          />
          <Toggle
            label="Show results to voters"
            checked={v.showResults}
            onChange={(b) => patch({ showResults: b })}
          />
          <Hint>
            {v.allowMultiple
              ? "Visitors can vote for more than one option."
              : "One vote per visitor — re-votes replace the previous choice."}
          </Hint>
        </EditorSection>
        {/* fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
            reuses the SAME QuadStepper Text/Image already use for their
            Padding/Margin control (BlockEditorPane/controls.jsx). */}
        <EditorSection heading="Spacing" dense>
          <QuadStepper label="Margin" value={v.margin} onChange={setMarginSide} />
        </EditorSection>
      </AdvancedOptions>
      </BlockEditorPane>
    </DndProvider>
  );
};

export default PollEditor;
